import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import api from "../api";
import { LayoutDashboard, MapPin, MessageCircle, Bell, UserCircle2, Heart } from "lucide-react";

function NavItem({ to, icon: Icon, label, active, badge, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 px-1 transition-colors ${
        active ? "text-sky-700" : "text-slate-500 hover:text-sky-600"
      }`}
    >
      <span className="relative">
        <Icon className={`w-5 h-5 ${active ? "stroke-[2.4]" : ""}`} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[9px] leading-none rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center font-semibold">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      <span className={`text-[10px] leading-tight ${active ? "font-semibold" : ""}`}>{label}</span>
      {active && <span className="absolute bottom-0 h-0.5 w-8 bg-sky-700 rounded-full" />}
    </Link>
  );
}

export default function MobileBottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  // Fall back to session cookie presence so the nav never flickers off during auth
  // re-fetch (e.g. when navigating to /notifications which causes a brief
  // auth context revalidation cycle).
  const hasSessionCookie = typeof document !== "undefined" && /(^|;\s*)csrf_token=/.test(document.cookie);
  const isLogged = !!user || hasSessionCookie;

  useEffect(() => {
    if (!isLogged) return;
    const reload = () => {
      api.get("/messages/unread-count").then((r) => setUnreadMsgs(r.data.unread || 0)).catch(() => {});
      api.get("/notifications").then((r) => setUnreadNotifs((r.data || []).filter((n) => !n.read).length)).catch(() => {});
    };
    reload();
    const id = setInterval(reload, 20000);
    return () => clearInterval(id);
  }, [isLogged]);

  // Hide on public auth pages even if token still present (post-logout flicker)
  const path = location.pathname;
  const publicPages = ["/", "/login", "/register"];
  if (publicPages.includes(path) && !user) return null;
  if (!isLogged) return null;

  const role = user?.role || "client";
  const dashLink = role === "mns" ? "/mns/dashboard" : role === "admin" ? "/admin" : "/dashboard";
  const isDash = path === "/dashboard" || path.startsWith("/mns/dashboard") || path.startsWith("/admin");
  const isMap = path.startsWith("/map");
  const isFavorites = path.startsWith("/favorites");
  const isMessages = path.startsWith("/messages");
  const isNotifs = path.startsWith("/notifications");
  const isProfile = path.startsWith("/profile");

  const items = [
    { key: "dashboard", to: dashLink, icon: LayoutDashboard, label: "Tableau", active: isDash, testid: "mnav-dashboard" },
    ...(role === "client"
      ? [
          { key: "map", to: "/map", icon: MapPin, label: "Carte", active: isMap, testid: "mnav-map" },
          { key: "favs", to: "/favorites", icon: Heart, label: "Favoris", active: isFavorites, testid: "mnav-favorites" },
        ]
      : []),
    { key: "messages", to: "/messages", icon: MessageCircle, label: "Messages", active: isMessages, badge: unreadMsgs, testid: "mnav-messages" },
    { key: "notifs", to: "/notifications", icon: Bell, label: "Notifs", active: isNotifs, badge: unreadNotifs, testid: "mnav-notifications" },
    { key: "profile", to: "/profile", icon: UserCircle2, label: "Profil", active: isProfile, testid: "mnav-profile" },
  ];

  return (
    <nav
      className="md:hidden mobile-bottom-nav bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-stretch justify-around relative">
        {items.map(({ key, ...rest }) => (
          <NavItem key={key} {...rest} />
        ))}
      </div>
    </nav>
  );
}
